import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import RequirePermission from "../../../auth/RequirePermission";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import AdminTableContainer from "../../../components/midpoint/admin/layout/AdminTableContainer";
import {
  fetchAdminDealers,
  runAdminDealerAction,
  runAdminDealerBulkAction,
  trackDealerEvent,
} from "../../../services/automapDealerOnboardingApi";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import {
  AdminDealerNav,
  ConfirmDialog,
  DealerFilters,
  formatDealerRow,
  statusBadgeClass,
} from "./dealerAdminShared";

function AdminDealersListPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkOwner, setBulkOwner] = useState("");
  const [confirmBulk, setConfirmBulk] = useState(null);

  const [search, setSearch] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [trialStatus, setTrialStatus] = useState("");
  const [planType, setPlanType] = useState("");
  const [assignedSalesOwner, setAssignedSalesOwner] = useState("");
  const [publicProfileEnabled, setPublicProfileEnabled] = useState("");
  const [hasActiveListings, setHasActiveListings] = useState("");
  const [hasStaleInventory, setHasStaleInventory] = useState("");
  const [trialEndingSoon, setTrialEndingSoon] = useState("");
  const [sort, setSort] = useState("recently_created");

  const loadDealers = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const params = { sort };
      if (search.trim()) params.search = search.trim();
      if (city.trim()) params.city = city.trim();
      if (status) params.status = status;
      if (verificationStatus) params.verificationStatus = verificationStatus;
      if (trialStatus) params.trialStatus = trialStatus;
      if (planType) params.planType = planType;
      if (assignedSalesOwner.trim()) params.assignedSalesOwner = assignedSalesOwner.trim();
      if (publicProfileEnabled) params.publicProfileEnabled = publicProfileEnabled;
      if (hasActiveListings) params.hasActiveListings = hasActiveListings;
      if (hasStaleInventory) params.hasStaleInventory = hasStaleInventory;
      if (trialEndingSoon) params.trialEndingSoon = trialEndingSoon;
      const data = await fetchAdminDealers(params);
      setDealers(data.dealers || []);
      trackDealerEvent("admin_dealers_viewed");
    } catch (err) {
      setErrorMessage(err.message || "No se pudo cargar la lista de concesionarios.");
      setDealers([]);
    } finally {
      setLoading(false);
    }
  }, [search, city, status, verificationStatus, trialStatus, planType, assignedSalesOwner, publicProfileEnabled, hasActiveListings, hasStaleInventory, trialEndingSoon, sort]);

  useEffect(() => { loadDealers(); }, [loadDealers]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === dealers.length) setSelected(new Set());
    else setSelected(new Set(dealers.map((d) => d.id)));
  };

  const runBulk = async (action) => {
    const dealerIds = [...selected];
    const payload = action === "assign_sales_owner" ? { assignedSalesOwner: bulkOwner } : {};
    await runAdminDealerBulkAction(dealerIds, action, payload);
    setSelected(new Set());
    setConfirmBulk(null);
    await loadDealers();
  };

  const rows = useMemo(() => dealers.map(formatDealerRow), [dealers]);

  return (
    <AdminPageLayout className="admin-dealers-page">
      <AdminAutomotiveNav active="dealers" />
      <AdminPageHeader
        eyebrow="AutoMapa · Concesionarios"
        title="Concesionarios"
        description="Gestión del ciclo de vida: onboarding, verificación, trials y operación."
        navigation={<AdminDealerNav active="list" />}
        primaryActions={
          <Link className="admin-btn admin-btn--primary" to="/admin/dealers/new">Nuevo concesionario</Link>
        }
      />
      <main className="admin-console__main">
        <DealerFilters
          search={search} city={city} status={status} verificationStatus={verificationStatus}
          trialStatus={trialStatus} planType={planType} assignedSalesOwner={assignedSalesOwner}
          publicProfileEnabled={publicProfileEnabled} hasActiveListings={hasActiveListings}
          hasStaleInventory={hasStaleInventory} trialEndingSoon={trialEndingSoon} sort={sort}
          onSearch={setSearch} onCity={setCity} onStatus={setStatus} onVerificationStatus={setVerificationStatus}
          onTrialStatus={setTrialStatus} onPlanType={setPlanType} onAssignedSalesOwner={setAssignedSalesOwner}
          onPublicProfileEnabled={setPublicProfileEnabled} onHasActiveListings={setHasActiveListings}
          onHasStaleInventory={setHasStaleInventory} onTrialEndingSoon={setTrialEndingSoon} onSort={setSort}
        />

        {canManage && selected.size > 0 ? (
          <div className="admin-console__header-actions" style={{ marginBottom: "1rem" }}>
            <input placeholder="Sales owner" value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)} />
            <button type="button" className="admin-btn" onClick={() => runBulk("assign_sales_owner")}>Asignar owner</button>
            <button type="button" className="admin-btn" onClick={() => runBulk("mark_verification_pending")}>Verificación pendiente</button>
            <button type="button" className="admin-btn" onClick={() => runBulk("enable_public_profile")}>Habilitar perfil</button>
            <button type="button" className="admin-btn" onClick={() => runBulk("disable_public_profile")}>Deshabilitar perfil</button>
            <button type="button" className="admin-btn" onClick={() => setConfirmBulk("pause_dealers")}>Pausar</button>
            <button type="button" className="admin-btn" onClick={() => setConfirmBulk("archive_dealers")}>Archivar</button>
          </div>
        ) : null}

        {errorMessage ? <AdminStatePanel variant="empty" message={errorMessage} /> : null}
        {loading ? (
          <AdminStatePanel variant="empty" message="Obteniendo concesionarios…" />
        ) : rows.length === 0 ? (
          <AdminStatePanel variant="empty" message="Aún no hay registros. Crea uno o convierte un prospecto." />
        ) : (
          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{canManage ? <input type="checkbox" checked={selected.size === dealers.length && dealers.length > 0} onChange={toggleAll} /> : null}</th>
                  <th>Nombre</th><th>Ciudad</th><th>Estado</th><th>Verificación</th><th>Trial</th><th>Plan</th><th>Público</th>
                  <th>Listings</th><th>Leads</th><th>WhatsApp</th><th>Owner</th><th>Actividad</th><th>Creado</th><th />
                </tr>
              </thead>
              <tbody>
                {dealers.map((dealer) => {
                  const row = formatDealerRow(dealer);
                  return (
                    <tr key={dealer.id}>
                      <td>{canManage ? <input type="checkbox" checked={selected.has(dealer.id)} onChange={() => toggleSelect(dealer.id)} /> : null}</td>
                      <td><Link to={`/admin/dealers/${dealer.id}`}>{row.name}</Link></td>
                      <td>{row.city}</td>
                      <td><span className={`admin-badge ${statusBadgeClass(dealer.status)}`}>{row.status}</span></td>
                      <td><span className={`admin-badge ${statusBadgeClass(dealer.verificationStatus)}`}>{row.verification}</span></td>
                      <td><span className={`admin-badge ${statusBadgeClass(dealer.trialStatus)}`}>{row.trial}</span></td>
                      <td>{row.plan}</td><td>{row.publicProfile}</td><td>{row.listings}</td><td>{row.leads}</td>
                      <td>{row.whatsapp}</td><td>{row.owner}</td><td>{row.lastActivity}</td><td>{row.created}</td>
                      <td className="admin-table__actions">
                        <Link to={`/admin/dealers/${dealer.id}`}>Ver</Link>
                        <Link to={`/admin/dealers/${dealer.id}/edit`}>Editar</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </main>
      <ConfirmDialog
        open={Boolean(confirmBulk)}
        title="Confirmar acción masiva"
        message={`¿Aplicar ${confirmBulk} a ${selected.size} concesionario(s)?`}
        onCancel={() => setConfirmBulk(null)}
        onConfirm={() => runBulk(confirmBulk)}
      />
    </AdminPageLayout>
  );
}

export default function AdminDealersListPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminDealersListPageContent />
    </RequirePermission>
  );
}
